import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { ApiClientError } from "@/services";
import { useUIStore } from "@/stores";

export const queryClient = new QueryClient({
	queryCache: new QueryCache({
		onError: (error, query) => {
			// biome-ignore lint/suspicious/noConsole: Error logging for debugging/monitoring
			console.error("Query error:", {
				queryKey: query.queryKey,
				error:
					error instanceof ApiClientError
						? {
								status: error.status,
								code: error.code,
								message: error.message,
							}
						: error,
			});

			// Show toast notification for server errors (500+)
			if (error instanceof ApiClientError && error.status >= 500) {
				useUIStore.getState().addToast({
					type: "error",
					title: "Error del servidor",
					description: "Ocurrio un problema al cargar los datos. Por favor, intente mas tarde.",
				});
			}
		},
	}),
	mutationCache: new MutationCache({
		onError: (error, _variables, _context, mutation) => {
			// biome-ignore lint/suspicious/noConsole: Error logging for debugging/monitoring
			console.error("Mutation error:", {
				mutationKey: mutation.options.mutationKey,
				error:
					error instanceof ApiClientError
						? {
								status: error.status,
								code: error.code,
								message: error.message,
								details: error.details,
							}
						: error,
			});

			// Show toast notification for server errors (500+)
			// Note: 4xx errors are typically handled by individual mutation onError callbacks
			if (error instanceof ApiClientError && error.status >= 500) {
				useUIStore.getState().addToast({
					type: "error",
					title: "Error del servidor",
					description: "No se pudo completar la operacion. Por favor, intente mas tarde.",
				});
			}
		},
	}),
	defaultOptions: {
		queries: {
			// Stale time: 5 minutes
			staleTime: 5 * 60 * 1000,

			// Cache time: 10 minutes
			gcTime: 10 * 60 * 1000,

			// Retry configuration
			retry: (failureCount, error) => {
				// Don't retry on 4xx errors (except 408 Request Timeout)
				if (error instanceof ApiClientError) {
					if (error.status >= 400 && error.status < 500 && error.status !== 408) {
						return false;
					}
				}
				// Retry up to 3 times for other errors
				return failureCount < 3;
			},

			// Refetch on window focus (good for keeping data fresh)
			refetchOnWindowFocus: true,

			// Don't refetch on mount if data is fresh
			refetchOnMount: true,
		},
		mutations: {
			// Don't retry mutations by default
			retry: false,
		},
	},
});

// =============================================================================
// Query Keys Factory
// =============================================================================

export const queryKeys = {
	// Auth
	auth: {
		me: ["auth", "me"] as const,
	},

	// Assets
	assets: {
		all: ["assets"] as const,
		lists: () => [...queryKeys.assets.all, "list"] as const,
		list: (filters?: Record<string, unknown>) => [...queryKeys.assets.lists(), filters] as const,
		details: () => [...queryKeys.assets.all, "detail"] as const,
		detail: (id: string) => [...queryKeys.assets.details(), id] as const,
		maintenances: (id: string) => [...queryKeys.assets.detail(id), "maintenances"] as const,
		movements: (id: string) => [...queryKeys.assets.detail(id), "movements"] as const,
	},

	// Asset Types
	assetTypes: {
		all: ["assetTypes"] as const,
		lists: () => [...queryKeys.assetTypes.all, "list"] as const,
		list: (filters?: Record<string, unknown>) =>
			[...queryKeys.assetTypes.lists(), filters] as const,
		details: () => [...queryKeys.assetTypes.all, "detail"] as const,
		detail: (id: string) => [...queryKeys.assetTypes.details(), id] as const,
	},

	// Locations
	locations: {
		all: ["locations"] as const,
		lists: () => [...queryKeys.locations.all, "list"] as const,
		list: (filters?: Record<string, unknown>) => [...queryKeys.locations.lists(), filters] as const,
		tree: () => [...queryKeys.locations.all, "tree"] as const,
		details: () => [...queryKeys.locations.all, "detail"] as const,
		detail: (id: string) => [...queryKeys.locations.details(), id] as const,
	},

	// Maintenances
	maintenances: {
		all: ["maintenances"] as const,
		lists: () => [...queryKeys.maintenances.all, "list"] as const,
		list: (filters?: Record<string, unknown>) =>
			[...queryKeys.maintenances.lists(), filters] as const,
		details: () => [...queryKeys.maintenances.all, "detail"] as const,
		detail: (id: string) => [...queryKeys.maintenances.details(), id] as const,
	},

	// Movements
	movements: {
		all: ["movements"] as const,
		lists: () => [...queryKeys.movements.all, "list"] as const,
		list: (filters?: Record<string, unknown>) => [...queryKeys.movements.lists(), filters] as const,
		details: () => [...queryKeys.movements.all, "detail"] as const,
		detail: (id: string) => [...queryKeys.movements.details(), id] as const,
	},

	// Users
	users: {
		all: ["users"] as const,
		lists: () => [...queryKeys.users.all, "list"] as const,
		list: (filters?: Record<string, unknown>) => [...queryKeys.users.lists(), filters] as const,
		details: () => [...queryKeys.users.all, "detail"] as const,
		detail: (id: string) => [...queryKeys.users.details(), id] as const,
	},

	// Dashboard
	dashboard: {
		all: ["dashboard"] as const,
		stats: () => [...queryKeys.dashboard.all, "stats"] as const,
		alerts: () => [...queryKeys.dashboard.all, "alerts"] as const,
	},

	// Audit Logs
	auditLogs: {
		all: ["audit-logs"] as const,
		lists: () => [...queryKeys.auditLogs.all, "list"] as const,
		list: (filters?: Record<string, unknown>) => [...queryKeys.auditLogs.lists(), filters] as const,
		asset: (assetId: string) => [...queryKeys.auditLogs.all, "asset", assetId] as const,
	},
} as const;
